import React, { useState, Component } from 'react';
import { Building2, Plus, Settings, ChevronDown, Search, Globe, Server, GitBranch, CreditCard, Database, Mail, Shield, CircleDot, Moon } from 'lucide-react';
import { SpaceStatus } from '../types/space';
interface SpaceItem {
  id: string;
  name: string;
  status: SpaceStatus;
  description: string;
  lastActivity: string;
  unread?: boolean;
}
interface AppItem {
  id: string;
  name: string;
  icon: React.ReactNode;
  status: 'connected' | 'error' | 'syncing';
  detail: string;
}
const SPACES: SpaceItem[] = [{
  id: 'sp-1',
  name: 'Product Launch',
  status: 'active',
  description: 'Landing page & marketing site',
  lastActivity: '2m ago'
}, {
  id: 'sp-2',
  name: 'Sales Pipeline',
  status: 'active',
  description: 'Q4 deals & outreach',
  lastActivity: '15m ago',
  unread: true
}, {
  id: 'sp-3',
  name: 'Q4 Board Deck',
  status: 'active',
  description: 'Investor update & financials',
  lastActivity: '1h ago'
}, {
  id: 'sp-6',
  name: 'Marketing Campaign',
  status: 'active',
  description: 'v2.0 launch across all channels',
  lastActivity: '20m ago',
  unread: true
}, {
  id: 'sp-8',
  name: 'WordPress Blog',
  status: 'active',
  description: 'blog.acme.com · Content & SEO',
  lastActivity: '5m ago'
}, {
  id: 'sp-4',
  name: 'Customer Onboarding',
  status: 'active',
  description: 'Trial conversion & email sequences',
  lastActivity: '3h ago',
  unread: true
}, {
  id: 'sp-5',
  name: 'Hiring: Senior PM',
  status: 'sleeping',
  description: '3 finalists · Interviews next week',
  lastActivity: '3d ago'
}, {
  id: 'sp-7',
  name: 'Expense Reports',
  status: 'planning',
  description: 'October spend review',
  lastActivity: 'Yesterday'
}];
const APPS: AppItem[] = [{
  id: 'app-1',
  name: 'HubSpot',
  icon: <CreditCard className="h-3.5 w-3.5" data-id="element-132" />,
  status: 'connected',
  detail: '23 active deals · $847k pipeline'
}, {
  id: 'app-2',
  name: 'Stripe',
  icon: <CreditCard className="h-3.5 w-3.5" data-id="element-133" />,
  status: 'connected',
  detail: 'Live mode · $124.8k MRR'
}, {
  id: 'app-3',
  name: 'WordPress',
  icon: <Globe className="h-3.5 w-3.5" data-id="element-134" />,
  status: 'connected',
  detail: 'blog.acme.com · 47 posts'
}, {
  id: 'app-4',
  name: 'Google Workspace',
  icon: <Mail className="h-3.5 w-3.5" data-id="element-135" />,
  status: 'connected',
  detail: '34 users · Drive, Docs, Sheets'
}, {
  id: 'app-5',
  name: 'Slack',
  icon: <Server className="h-3.5 w-3.5" data-id="element-136" />,
  status: 'connected',
  detail: 'acme-corp · 12 channels'
}, {
  id: 'app-6',
  name: 'GitHub',
  icon: <GitBranch className="h-3.5 w-3.5" data-id="element-137" />,
  status: 'syncing',
  detail: 'acme/product · 3 open PRs'
}, {
  id: 'app-7',
  name: 'Resend',
  icon: <Mail className="h-3.5 w-3.5" data-id="element-138" />,
  status: 'connected',
  detail: '8,400 subscribers'
}];
interface NavigationSidebarProps {
  activeSpaceId: string;
  onSelectSpace: (id: string) => void;
}
export function NavigationSidebar({
  activeSpaceId,
  onSelectSpace
}: NavigationSidebarProps) {
  return <div className="w-[272px] flex-shrink-0 flex flex-col h-full bg-gray-50 border-r border-gray-200 text-gray-900" data-id="element-139">
      {/* Organization Header */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors" data-id="element-140">
        <div className="flex items-center space-x-2.5 overflow-hidden" data-id="element-141">
          <div className="h-7 w-7 rounded-lg bg-indigo-600 flex items-center justify-center text-white flex-shrink-0 text-xs font-bold shadow-sm" data-id="element-142">
            A
          </div>
          <div className="flex flex-col min-w-0" data-id="element-143">
            <span className="font-semibold text-sm truncate" data-id="element-144">Acme Corp</span>
            <span className="text-[10px] text-gray-500" data-id="element-145">12 members</span>
          </div>
        </div>
        <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-146" />
      </div>

      {/* Search */}
      <div className="px-3 py-3" data-id="element-147">
        <button className="w-full flex items-center px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-gray-500 hover:border-gray-300 transition-colors shadow-sm" data-id="element-148">
          <Search className="h-3.5 w-3.5 mr-2 text-gray-400" data-id="element-149" />
          Search spaces...
          <span className="ml-auto text-[10px] text-gray-400 border border-gray-200 rounded px-1 py-0.5" data-id="element-150">
            ⌘K
          </span>
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-5" data-id="element-151">
        {/* Spaces */}
        <div data-id="element-152">
          <div className="flex items-center justify-between px-2 mb-1.5" data-id="element-153">
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-154">
              Spaces
            </span>
            <button className="h-5 w-5 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors" data-id="element-155">
              <Plus className="h-3.5 w-3.5" data-id="element-156" />
            </button>
          </div>
          <div className="space-y-0.5" data-id="element-157">
            {SPACES.map(space => <SpaceNavItem key={space.id} space={space} active={space.id === activeSpaceId} onClick={() => onSelectSpace(space.id)} data-id="element-158" />)}
          </div>
        </div>

        {/* Apps & Resources */}
        <div data-id="element-159">
          <div className="flex items-center justify-between px-2 mb-1.5" data-id="element-160">
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-161">
              Connected Apps
            </span>
            <button className="h-5 w-5 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors" data-id="element-162">
              <Plus className="h-3.5 w-3.5" data-id="element-163" />
            </button>
          </div>
          <div className="space-y-0.5" data-id="element-164">
            {APPS.map(app => <AppNavItem key={app.id} app={app} data-id="element-165" />)}
          </div>
        </div>
      </div>

      {/* User Footer */}
      <div className="p-2 border-t border-gray-200" data-id="element-166">
        <div className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors group" data-id="element-167">
          <div className="flex items-center space-x-2.5" data-id="element-168">
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-[10px] text-white font-bold shadow-sm" data-id="element-169">
              JD
            </div>
            <div className="flex flex-col" data-id="element-170">
              <span className="text-xs font-medium text-gray-800" data-id="element-171">
                John Doe
              </span>
              <span className="text-[10px] text-gray-500" data-id="element-172">john@acme.com</span>
            </div>
          </div>
          <Settings className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" data-id="element-173" />
        </div>
      </div>
    </div>;
}
function SpaceNavItem({
  space,
  active,
  onClick
}: {
  space: SpaceItem;
  active: boolean;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`
        w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-all text-left
        ${active ? 'bg-white shadow-sm ring-1 ring-gray-200/80' : 'hover:bg-gray-100/80'}
      `} data-id="element-174">
      {/* Status Dot */}
      <div className="mt-1.5 flex-shrink-0" data-id="element-175">
        {space.status === 'active' && <span className="relative flex h-2 w-2" data-id="element-176">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-50" data-id="element-177"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-178"></span>
          </span>}
        {space.status === 'planning' && <div className="h-2 w-2 rounded-full bg-gray-400" data-id="element-179" />}
        {space.status === 'sleeping' && <div className="h-2 w-2 rounded-full bg-gray-300" data-id="element-180" />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0" data-id="element-181">
        <div className="flex items-center justify-between gap-2" data-id="element-182">
          <div className="flex items-center min-w-0 gap-1.5" data-id="element-183">
            <span className={`text-sm truncate ${active ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`} data-id="element-184">
              {space.name}
            </span>
            {space.unread && <span className="flex-shrink-0 h-1.5 w-1.5 rounded-full bg-blue-500" data-id="element-185" />}
          </div>
          <span className="text-[10px] text-gray-400 flex-shrink-0 tabular-nums" data-id="element-186">
            {space.lastActivity}
          </span>
        </div>
        <p className="text-xs text-gray-500 truncate mt-0.5" data-id="element-187">
          {space.description}
        </p>
      </div>
    </button>;
}
function AppNavItem({
  app
}: {
  app: AppItem;
}) {
  return <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-100/80 cursor-pointer transition-colors group" data-id="element-188">
      {/* Icon */}
      <div className={`
        h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0
        ${app.status === 'error' ? 'bg-red-100 text-red-500' : 'bg-gray-200/80 text-gray-500 group-hover:bg-gray-200'}
      `} data-id="element-189">
        {app.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0" data-id="element-190">
        <div className="flex items-center gap-1.5" data-id="element-191">
          <span className="text-sm font-medium text-gray-700 truncate" data-id="element-192">
            {app.name}
          </span>
          {app.status === 'connected' && <div className="h-1.5 w-1.5 rounded-full bg-green-400 flex-shrink-0" data-id="element-193" />}
          {app.status === 'syncing' && <div className="h-1.5 w-1.5 rounded-full bg-amber-400 flex-shrink-0 animate-pulse" data-id="element-194" />}
          {app.status === 'error' && <div className="h-1.5 w-1.5 rounded-full bg-red-400 flex-shrink-0" data-id="element-195" />}
        </div>
        <p className="text-[11px] text-gray-500 truncate" data-id="element-196">{app.detail}</p>
      </div>
    </div>;
}